/* eslint-disable no-undef */
import * as PropTypes from "prop-types";
import * as utils from "./utils";
import React, { memo } from "react";
import Draggable from "react-draggable";
import store from "../PDFEditor/store";

const VIEWX = 80;

function updatePosition(pos, scale) {
  return { x: (pos.x || 0) * scale, y: (pos.y || 0) * scale };
}

const Points = (props) => {
  const { size, color, scale, label } = props;
  const [pos, setPos] = React.useState(updatePosition(props.pos, scale.scaleX));

  React.useEffect(() => {
    setPos(updatePosition(props.pos, scale.scaleX));
  }, [props]);

  function onDrag(e, ui) {
    props.onDrag({
      x: (ui.x - size / 2) / scale.scaleX,
      y: (ui.y - size / 2) / scale.scaleY,
    });
  }

  return (
    <Draggable
      //   defaultPosition={{ x: pos.x + size / 2, y: pos.y + size / 2 }}
      position={{ x: pos.x + size / 2, y: pos.y + size / 2 }}
      onDrag={onDrag}
      bounds="#container"
    >
      <div
        style={{
          width: size,
          height: size,
          top: -size / 2,
          left: -size / 2,
          background: color,
        }}
        className={`rounded-full absolute z-[33] cursor-pointer text-[#fff] text-[11px] font-semibold flex items-center justify-center`}
      >
        {label}
      </div>
    </Draggable>
  );
};

Points.propTypes = {
  size: PropTypes.number,
  color: PropTypes.string,
  pos: { x: PropTypes.number, y: PropTypes.number },
  onDrag: PropTypes.func,
  scale: PropTypes.object,
  label: PropTypes.node,
};

function updatePoints(width, height, id, cache = true) {
  const aspectRatio = width / height;

  const VIEWY = VIEWX / aspectRatio;
  const [SCALEX, SCALEY] = [width / VIEWX, height / VIEWY];

  return (
    store.get("image-edit")?.[id]?.points || [
      { x: 0, y: 0 },
      { x: width / SCALEX, y: 0 },
      { x: width / SCALEX, y: height / SCALEY },
      { x: 0, y: height / SCALEY },
    ]
  );
}

const ImageEditorApp = React.forwardRef(function ImageEditor(props, ref) {
  const { width = 620, height = 620, vertexsize = 26, thickness = 0.26, color = "#224b99", image = {} } = props;
  const imageSrc = image?.data;

  const [ready, setReady] = React.useState(false);
  const [points, setPoints] = React.useState(updatePoints(width, height));

  React.useEffect(() => {
    if (typeof width === "number" && !isNaN(width) && typeof height === "number" && !isNaN(height)) {
      setPoints(updatePoints(width, height, image?.id));
      setReady(true);
    }
  }, [width, height, image]);

  React.useEffect(() => {
    if (!ready) return;

    const canvas = document.getElementById("canvas");
    const ctx = canvas.getContext("2d");

    const img = new Image(width, height);
    img.src = imageSrc;

    img.onload = function () {
      canvas.width = width;
      canvas.height = height;

      //   console.log(width, height, this.naturalWidth);
      ctx.drawImage(img, 0, 0, this.naturalWidth, this.naturalHeight, 0, 0, width, height);
    };
  }, [ready, imageSrc, width, height]);

  function handleDrag(idx, pos) {
    const points_ = [...points];
    points_[idx] = pos;
    setPoints(points_);
    const existingData = store.get("image-edit") || {};
    store.update("image-edit", { ...existingData, [image.id]: { ...existingData[image.id], points: points_ } });
  }

  React.useImperativeHandle(
    ref,
    () => ({
      crop: handleCapture,
    }),
    [imageSrc, width, height, points]
  );

  async function handleCapture() {
    //   const { data, img } = await clip(points);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const img = new Image(width, height);
    img.src = imageSrc;
    new Promise((resolve, reject) => {
      img.onload = function () {
        canvas.width = this.width;
        canvas.height = this.height;

        ctx.drawImage(img, 0, 0, this.naturalWidth, this.naturalHeight, 0, 0, width, height);
        resolve(canvas);
      };
    })
      .then((canvas_) => {
        const src = cv.imread(canvas_);
        const pointsArr = points.flatMap((obj) => Object.values(obj).map((d) => (d * width) / VIEWX));

        const actualWidth = Math.max(utils.distance(points[0], points[1]), utils.distance(points[2], points[3]));
        const actualHeight = Math.max(utils.distance(points[0], points[3]), utils.distance(points[1], points[2]));
        const scaleProportion = actualWidth > actualHeight ? width / actualWidth : height / actualHeight;

        const [cropWidth, cropHeight] = [actualWidth * scaleProportion, actualHeight * scaleProportion];
        console.log(actualWidth, actualHeight, scaleProportion, pointsArr);
        const dst = new cv.Mat();
        const dsize = new cv.Size(cropWidth, cropHeight);

        const stri = cv.matFromArray(4, 1, cv.CV_32FC2, pointsArr);
        const dtri = cv.matFromArray(4, 1, cv.CV_32FC2, [0, 0, cropWidth, 0, cropWidth, cropHeight, 0, cropHeight]);

        const M = cv.getPerspectiveTransform(stri, dtri);
        cv.warpPerspective(src, dst, M, dsize, cv.INTER_LINEAR, cv.BORDER_CONSTANT, new cv.Scalar());

        cv.imshow("canvas", dst);
        setPoints(updatePoints(width, height));
        src.delete();
        // dst.delete();
        M.delete();
        stri.delete();
        dtri.delete();
        //   const image = cv.imencode(".png", dst);
        //   console.log(dst, image);
        const existingData = store.get("image-edit") || {};
        delete existingData[image.id];
        store.update("image-edit", existingData);
        return { cropWidth, cropHeight, dst };
      })
      .then(({ cropWidth, cropHeight, dst: mat }) => {
        const img = new cv.Mat();
        const depth = mat.type() % 8;
        const scale = depth <= cv.CV_8S ? 1 : depth <= cv.CV_32S ? 1 / 256 : 255;
        const shift = depth === cv.CV_8S || depth === cv.CV_16S ? 128 : 0;
        mat.convertTo(img, cv.CV_8U, scale, shift);

        switch (img.type()) {
          case cv.CV_8UC1:
            cv.cvtColor(img, img, cv.COLOR_GRAY2RGBA);
            break;
          case cv.CV_8UC3:
            cv.cvtColor(img, img, cv.COLOR_RGB2RGBA);
            break;
          case cv.CV_8UC4:
            break;
          default:
            throw new Error("Bad number of channels (Source image must have 1, 3 or 4 channels)");
        }
        const imgData = new ImageData(new Uint8ClampedArray(img.data), img.cols, img.rows);
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvas.width = imgData.width;
        canvas.height = imgData.height;
        ctx.putImageData(imgData, 0, 0);
        img.delete();
        const data = canvas.toDataURL();
        console.log(data, ctx.getImageData(0, 0, cropWidth, cropHeight));
        mat.delete();
        props?.onChange?.({ ...image, data });
      });
  }

  return (
    <React.Fragment>
      <div className="flex items-center justify-center w-full grow-0">
        <div
          id="container"
          style={{
            width: width + vertexsize,
            height: height + vertexsize,
          }}
        >
          <div style={{ width, height }} className="relative flex items-center justify-center">
            <canvas
              id="canvas"
              style={{
                translate: `${utils.unitParser(vertexsize / 2)} ${utils.unitParser(vertexsize / 2)}`,
                // width,
                overflow: "hidden",
                // height,
              }}
            ></canvas>
            {ready && (
              <>
                {points.map((p, idx) => (
                  <Points
                    key={idx}
                    label={[1, 2, 4, 3][idx]}
                    size={vertexsize}
                    color={color}
                    pos={p}
                    scale={{ scaleX: width / VIEWX, scaleY: width / VIEWX }}
                    config={{ ...props.config }}
                    onDrag={(pos) => handleDrag(idx, pos)}
                  />
                ))}
                <svg
                  style={{
                    position: "absolute",
                    top: vertexsize / 2,
                    left: vertexsize / 2,
                    width,
                    // height,
                  }}
                  viewBox={`0 0 ${VIEWX} ${(VIEWX * height) / width}`}
                >
                  <path
                    fillRule="nonzero"
                    stroke="none"
                    fill="#333333af"
                    d={`M0,0 h${VIEWX} v${(VIEWX * height) / width} h-${VIEWX} z
                  M${points[0].x},${points[0].y}
                  L${points[3].x},${points[3].y}
                  L${points[2].x},${points[2].y}
                  L${points[1].x},${points[1].y}
                  z
              `}
                  />
                  <polygon points={points.map((p) => `${p.x},${p.y}`)} stroke={color} fill="none" strokeWidth={thickness} />
                </svg>
              </>
            )}
          </div>
        </div>
      </div>
      {/* 
      <button onClick={handleCapture}>capture</button>

      <div id="preview"></div>
      <canvas id="result" /> */}
    </React.Fragment>
  );
});
const ImageEditor = memo(ImageEditorApp);
export default ImageEditor;
